import { Suspense, lazy } from 'react';
import type { XkEvent, XkEventGroup, XkStation, XkStock } from '@/lib/api/types';
import { useFormPanel } from './formPanelStore';
import { ProjectForm } from '@/features/brand/ProjectForm';
import { XotskiForm } from '@/features/stock/XotskiForm';
import type { NewXotski } from '@/features/stock/AddXotskiDialog';
import { EventForm } from '@/features/events/EventForm';
import { StationForm } from '@/features/stations/StationForm';
import { ProjectEditForm } from '@/features/project/ProjectEditForm';

// Lazy — el marco de la vista previa sólo se carga al abrirla,
// keeping it out of the always-mounted layout chunk.
// Lazy for the same reason as the preview: the allocation editor pulls in the
// planner's dnd stack, which the layout chunk should not carry by default.
const StationAllocationsPanel = lazy(() =>
  import('@/features/project/planner/StationAllocationsPanel').then((m) => ({
    default: m.StationAllocationsPanel,
  })),
);

// Event properties + reporters, shown in the rail when an event is selected
// on the board. Lazy for the same reason as the others.
const EventSummaryPanel = lazy(() =>
  import('@/features/events/EventSummaryPanel').then((m) => ({
    default: m.EventSummaryPanel,
  })),
);
const EventContextPanel = lazy(() =>
  import('@/features/project/planner/EventContextPanel').then((m) => ({
    default: m.EventContextPanel,
  })),
);

// La previa de una estación es el enlace público real en un marco. No hay
// componente intermedio: lo hubo mientras la previa era una maqueta que
// reconstruía la encuesta, y al pasar a enseñar el enlace de verdad se quedó en
// un envoltorio de una línea.
const XurveyPreviewFrame = lazy(() =>
  import('@/features/events/xurvey/XurveyPreviewFrame').then((m) => ({
    default: m.XurveyPreviewFrame,
  })),
);

/**
 * Renders the currently-active right-panel form (see `formPanelStore`). The
 * surrounding header/close chrome lives in `RightSidebar`; this component only
 * switches on `active.kind` and renders the matching form body, wiring its
 * success/cancel callbacks back to the store.
 *
 * When you migrate another dialog: widen `FormPanelKind`, extract its form
 * body (decoupled from the modal shell, like `ProjectForm`), and add a `case`.
 */
export function FormPanelHost() {
  const active = useFormPanel((s) => s.active);
  const closeForm = useFormPanel((s) => s.closeForm);

  if (!active) return null;

  // Cancel restores the rail; a successful save collapses it (the caller's
  // onDone usually redirects to the new entity first).
  const cancel = () => closeForm();
  const done = (result: unknown) => {
    active.onDone?.(result);
    closeForm({ collapse: true });
  };

  switch (active.kind) {
    case 'project': {
      const brandId = Number(active.context?.brandId);
      return (
        <ProjectForm
          brandId={brandId}
          onCancel={cancel}
          onCreated={(g: XkEventGroup) => done(g)}
        />
      );
    }
    case 'project-edit': {
      const group = active.context?.group as XkEventGroup | undefined;
      if (!group) return null;
      return (
        <ProjectEditForm
          group={group}
          onCancel={cancel}
          onSaved={(g: XkEventGroup) => done(g)}
          onDeleted={() => done({ deleted: true })}
        />
      );
    }
    case 'xotski': {
      const brandId = Number(active.context?.brandId);
      const stockId = active.context?.stockId
        ? Number(active.context.stockId)
        : undefined;
      const initial = (active.context?.initial as Partial<NewXotski> | undefined) ?? null;
      return (
        <XotskiForm
          brandId={brandId}
          stockId={stockId}
          initial={initial}
          onCancel={cancel}
          onSaved={(s: XkStock) => done(s)}
        />
      );
    }
    case 'event': {
      const brandId = Number(active.context?.brandId);
      const projectId = active.context?.projectId
        ? Number(active.context.projectId)
        : undefined;
      const initialDate =
        typeof active.context?.initialDate === 'string'
          ? active.context.initialDate
          : undefined;
      return (
        <EventForm
          brandId={brandId}
          projectId={projectId}
          initialDate={initialDate}
          onCancel={cancel}
          onCreated={(e: XkEvent) => done(e)}
        />
      );
    }
    case 'station': {
      const station = active.context?.station as XkStation | undefined;
      if (!station) return null;
      return (
        <StationForm
          station={station}
          onCancel={cancel}
          onSaved={(s: XkStation) => done(s)}
          onDeleted={() => done({ deleted: true })}
        />
      );
    }
    case 'station-allocations': {
      const stationId = Number(active.context?.stationId);
      const eventId = Number(active.context?.eventId);
      const brandId = Number(active.context?.brandId);
      const companyId = Number(active.context?.companyId);
      return (
        <Suspense fallback={<div className="flex-1 p-4 text-center text-xs text-[var(--color-xk-text-muted)]">…</div>}>
          <StationAllocationsPanel
            stationId={stationId}
            eventId={eventId}
            brandId={brandId}
            companyId={companyId}
          />
        </Suspense>
      );
    }
    case 'event-properties': {
      const eventId = Number(active.context?.eventId);
      const groupId = Number(active.context?.groupId);
      const brandId = Number(active.context?.brandId);
      return (
        <Suspense fallback={<div className="flex-1 p-4 text-center text-xs text-[var(--color-xk-text-muted)]">…</div>}>
          <EventContextPanel
            eventId={eventId}
            groupId={groupId}
            brandId={brandId}
            projectStart={active.context?.projectStart as string | null | undefined}
            projectEnd={active.context?.projectEnd as string | null | undefined}
          />
        </Suspense>
      );
    }
    case 'event-summary': {
      const eventId = Number(active.context?.eventId);
      return (
        <Suspense fallback={<div className="flex-1 p-4 text-center text-xs text-[var(--color-xk-text-muted)]">…</div>}>
          <EventSummaryPanel eventId={eventId} />
        </Suspense>
      );
    }
    case 'station-preview': {
      const eventId = Number(active.context?.eventId);
      const stationId = Number(active.context?.stationId);
      return (
        <Suspense fallback={<div className="flex-1 p-4 text-center text-xs text-[var(--color-xk-text-muted)]">…</div>}>
          <XurveyPreviewFrame eventId={eventId} stationId={stationId} />
        </Suspense>
      );
    }
    default:
      return null;
  }
}
